import { settingFieldType } from 'config/settingFieldType';
import isEqual from 'lodash/isEqual';

// Field types whose value is stored as a JSON string.
export const JSON_FIELD_TYPES = [
	settingFieldType.MULTISELECT,
	settingFieldType.MULTIVALUE,
	settingFieldType.CREDENTIAL
];

// Parses a stored JSON string (tolerating line breaks); `fallback` when it is invalid or null.
export const safeParseJSON = (str, fallback = null) => {
	try {
		const cleaned = typeof str === 'string' ? str.replace(/[\n\r\t]+\s*/g, '') : str;
		return JSON.parse(cleaned) ?? fallback;
	} catch {
		return fallback;
	}
};

// Raw stored string → the value controls work with; null when a JSON type fails to parse.
export const parseSettingValue = (fieldTypeID, raw) => {
	if (JSON_FIELD_TYPES.includes(fieldTypeID)) return safeParseJSON(raw);
	if (fieldTypeID === settingFieldType.BOOLEAN) return parseFloat(raw);
	return raw;
};

const hasStoredValue = (item) => !!item && item.value?.length > 0;

export const mergedSettings = (defaultSettings, settings) => {
	return defaultSettings?.map((defaultSetting) => {
		const matchingItem = settings?.find((setting) => setting.defaultSettingID === defaultSetting.defaultSettingID);
		const { fieldTypeID } = defaultSetting;
		const raw = hasStoredValue(matchingItem) ? matchingItem.value : defaultSetting.defaultValue;

		const parsesValue = JSON_FIELD_TYPES.includes(fieldTypeID) || fieldTypeID === settingFieldType.BOOLEAN;
		const parsedValue = parsesValue ? parseSettingValue(fieldTypeID, raw) : null;

		const parsesOptions = [settingFieldType.SINGLESELECT, settingFieldType.MULTISELECT].includes(fieldTypeID);
		const parsedValueOptions = parsesOptions ? safeParseJSON(defaultSetting.valueOptions) || [] : [];

		return {
			...defaultSetting,
			valueOptions: parsedValueOptions.length > 0 ? parsedValueOptions : defaultSetting.valueOptions,
			value: parsedValue !== null ? parsedValue : raw,
			isValueFromDefault: !hasStoredValue(matchingItem)
		};
	});
};

// True when a merged row's value differs from its catalogue default; rows without a default never are.
export const isSettingChangedFromDefault = (setting) => {
	const { defaultValue, fieldTypeID, value } = setting || {};
	if (defaultValue === null || defaultValue === undefined || defaultValue === '') return false;

	if (fieldTypeID === settingFieldType.JSON) return !isEqual(safeParseJSON(value), safeParseJSON(defaultValue));
	const parsedDefault = parseSettingValue(fieldTypeID, defaultValue);
	if (typeof parsedDefault === 'object' && parsedDefault !== null) return !isEqual(value, parsedDefault);
	return String(value ?? '') !== String(parsedDefault ?? '');
};

// False for buttons and read-only (`disabled`) rows: their values are written by the system.
export const isSettingResettable = (setting) => {
	if (!setting || setting.fieldTypeID === settingFieldType.BUTTON) return false;
	const options = typeof setting.valueOptions === 'string' ? safeParseJSON(setting.valueOptions, {}) : null;
	return !options?.disabled;
};

// A group's section cards resolved to the rows a viewer can see. Rows not placed by id go to the
// first card whose `collects` accepts them, else the `collectsUnplaced` card, sorted by `rank`.
export const buildSettingsSections = ({
	sections,
	groupRows,
	settingsByID,
	placedIDs,
	canViewDeveloperRows,
	rank = () => 0
}) => {
	const unplaced = groupRows.filter((s) => !placedIDs.has(s.defaultSettingID));
	const owner = new Map();
	unplaced.forEach((setting) => {
		const target =
			sections.find((section) => section.collects?.(setting)) ||
			sections.find((section) => section.collectsUnplaced);
		if (target) owner.set(setting.defaultSettingID, target.key);
	});

	return sections
		.map((section) => {
			const collected = unplaced
				.filter((s) => owner.get(s.defaultSettingID) === section.key)
				.sort((a, b) => rank(a) - rank(b))
				.map((s) => ({ id: s.defaultSettingID, ...(section.developerOnly && { developerOnly: true }) }));
			const rows = [...section.rows, ...collected].filter((row) => {
				if (isDeveloperRow(row)) return canViewDeveloperRows;
				if (row.id) return settingsByID.has(row.id);
				return true;
			});
			return { ...section, rows };
		})
		.filter((section) => section.rows.length > 0);
};

export const isDeveloperRow = (row) => !!(row.proposed || row.developerOnly);

// Most important first after the module switch: connect, run, then tune.
const FIELD_TYPE_IMPORTANCE = [
	settingFieldType.CREDENTIAL,
	settingFieldType.BUTTON,
	settingFieldType.BOOLEAN,
	settingFieldType.SINGLESELECT,
	settingFieldType.MULTISELECT,
	settingFieldType.NUMBER,
	settingFieldType.STRING,
	settingFieldType.MULTIVALUE,
	settingFieldType.DATE,
	settingFieldType.FILEUPLOAD,
	settingFieldType.JSON
];

// Sort rank for rows no layout places by hand (lower first); module switches lead.
export const settingImportance = (setting, moduleFlags) => {
	if (moduleFlags.has(setting.defaultSettingID)) return -1;
	const rank = FIELD_TYPE_IMPORTANCE.indexOf(setting.fieldTypeID);
	return rank === -1 ? FIELD_TYPE_IMPORTANCE.length : rank;
};
